/**
 * PDP V3 — small, namespaced interactions for WEBLCFB2C-108.
 *
 * The theme's native web components continue to own variants and product forms. This
 * file only handles V3's colour drawer, the folded SEO text and product-link routing.
 */
(() => {
  const DRAWER_SELECTOR = '[data-pdp-v3-drawer]';
  let activeDrawer = null;
  let activeOpener = null;

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function openDrawer(drawer, opener) {
    if (!drawer) return;

    if (activeDrawer && activeDrawer !== drawer) closeDrawer(activeDrawer, false);

    // Le panneau sticky (safe-sticky) crée un stacking context qui piégeait le drawer
    // sous le header : on le sort vers <body> pour que son z-index redevienne global.
    if (drawer.parentElement !== document.body) document.body.appendChild(drawer);

    activeDrawer = drawer;
    activeOpener = opener;
    drawer.hidden = false;
    document.body.classList.add('pdp-v3-drawer-lock');
    opener?.setAttribute('aria-expanded', 'true');

    window.requestAnimationFrame(() => {
      drawer.querySelector('.pdp-v3-color-drawer__close')?.focus({ preventScroll: true });
    });
  }

  function closeDrawer(drawer = activeDrawer, restoreFocus = true) {
    if (!drawer) return;

    drawer.hidden = true;
    document.body.classList.remove('pdp-v3-drawer-lock');
    document
      .querySelectorAll(`[data-pdp-v3-drawer-open="${CSS.escape(drawer.id)}"]`)
      .forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));

    if (restoreFocus) activeOpener?.focus({ preventScroll: true });
    activeDrawer = null;
    activeOpener = null;
  }

  function parseMoneyToCents(value) {
    const normalized = value
      .replace(/[\s\u00a0\u202f]/g, '')
      .replace(/[^\d,.-]/g, '')
      .replace(',', '.');
    const amount = Number.parseFloat(normalized);

    return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
  }

  function selectedUpsellTotal() {
    return [...document.querySelectorAll('.pdp-v3-upsell [data-upsell-checkbox]:checked:not(:disabled)')]
      .reduce((total, checkbox) => {
        const item = checkbox.closest('.product-upsell__item');
        const selectedOption = item?.querySelector('[data-upsell-variant-select]')?.selectedOptions?.[0];
        const priceElement = item?.querySelector(
          '.product-upsell__price-sale, .product-upsell__price-regular'
        );
        const price = Number.parseInt(selectedOption?.dataset.price, 10)
          || parseMoneyToCents(priceElement?.textContent || '');

        return total + price;
      }, 0);
  }

  function syncStickyTotal() {
    const upsellTotal = selectedUpsellTotal();

    document.querySelectorAll('[data-pdp-v3-base-price]').forEach((price) => {
      const basePrice = Number.parseInt(price.dataset.pdpV3BasePrice, 10) || 0;

      if (upsellTotal === 0) {
        price.textContent = price.dataset.pdpV3BasePriceLabel;
        return;
      }

      const locale = document.documentElement.lang || 'fr-FR';
      const currency = window.Shopify?.currency?.active || 'EUR';
      const total = new Intl.NumberFormat(locale, { style: 'currency', currency })
        .format((basePrice + upsellTotal) / 100);
      price.textContent = `Total ${total}`;
    });
  }

  function syncStoryToggle(story) {
    const content = story.querySelector('[data-pdp-v3-story-content]');
    const toggle = story.querySelector('[data-pdp-v3-story-toggle]');
    if (!content || !toggle || story.classList.contains('is-expanded')) return;

    toggle.hidden = content.scrollHeight <= content.clientHeight + 1;
  }

  async function handleModelQuickAdd(button) {
    const picker = button.closest('variant-picker');
    const form = document.getElementById(picker?.getAttribute('form-id'));
    if (!form || button.disabled || !form.reportValidity()) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.closest('x-popover')?.hide?.();
    window.dfCartPending?.start();
    try {
      await window.dfCartActions.add({ form, variantId: button.dataset.pdpV3QuickAdd });
    } catch (error) {
      window.dfCartActions.report(error);
    } finally {
      window.dfCartPending?.stop();
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('change', (event) => {
    const stickyModel = event.target instanceof Element && event.target.closest('[data-pdp-v3-sticky-model]');
    if (!stickyModel) return;

    // Le select sticky pilote le radio du sélecteur natif : le vendor gère le reste (form, rerender).
    const radio = document.querySelector(
      `input[name="${CSS.escape(stickyModel.dataset.radioName)}"][value="${CSS.escape(stickyModel.value)}"]`
    );
    radio?.click();
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const quickAddButton = event.target.closest('[data-pdp-v3-quick-add]');
    if (quickAddButton) {
      event.preventDefault();
      event.stopPropagation();
      handleModelQuickAdd(quickAddButton);
      return;
    }

    const bisButton = event.target.closest('[data-pdp-v3-bis]');
    if (bisButton) {
      event.preventDefault();
      window.BIS?.popup?.show?.();
      return;
    }

    const stickyAtc = event.target.closest('[data-pdp-v3-sticky-atc]');
    if (stickyAtc && !stickyAtc.disabled) {
      // La barre sticky soumet le formulaire principal : même ajout, même tiroir que le bouton principal.
      event.preventDefault();
      const form = document.forms[stickyAtc.dataset.productForm];
      const submit = form?.querySelector('[type=submit]');
      if (submit?.getAttribute('aria-busy') !== 'true') form?.requestSubmit(submit);
      return;
    }


    const opener = event.target.closest('[data-pdp-v3-drawer-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(document.getElementById(opener.dataset.pdpV3DrawerOpen), opener);
      return;
    }

    const closeButton = event.target.closest('[data-pdp-v3-drawer-close]');
    if (closeButton) {
      event.preventDefault();
      closeDrawer(closeButton.closest(DRAWER_SELECTOR));
      return;
    }

    const storyToggle = event.target.closest('[data-pdp-v3-story-toggle]');
    if (storyToggle) {
      const story = storyToggle.closest('[data-pdp-v3-story]');
      const isExpanded = story?.classList.toggle('is-expanded') ?? false;

      storyToggle.setAttribute('aria-expanded', String(isExpanded));
      storyToggle.textContent = isExpanded ? storyToggle.dataset.less : storyToggle.dataset.more;
      return;
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (!activeDrawer) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = [...activeDrawer.querySelectorAll(focusableSelector)]
      .filter((element) => !element.closest('[hidden]'));
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('change', (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.matches('[data-upsell-checkbox], [data-upsell-variant-select]')) {
      window.requestAnimationFrame(syncStickyTotal);
    }
  });

  window.addEventListener('pageshow', () => window.requestAnimationFrame(syncStickyTotal));
  document.addEventListener('product:rerender', () => window.requestAnimationFrame(syncStickyTotal), true);

  document.addEventListener('variant:change', () => {
    window.requestAnimationFrame(syncStickyTotal);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (activeDrawer && event.target.contains(activeDrawer)) closeDrawer(activeDrawer, false);
  });

  document.querySelectorAll('[data-pdp-v3-story]').forEach((story) => {
    syncStoryToggle(story);
    new ResizeObserver(() => syncStoryToggle(story)).observe(
      story.querySelector('[data-pdp-v3-story-content]')
    );
  });

  syncStickyTotal();
})();
